import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus } from 'lucide-react';
import CTASection from '../components/CTASection';
import usePageContent from '../hooks/usePageContent';
import useSEO from '../hooks/useSEO';

const RELATED = [
    { to: '/shop', label: 'Shop equipment', key: 'shop' },
    { to: '/rental', label: 'Rental & training', key: 'rental' },
    { to: '/services', label: 'Service & support', key: 'support' },
    { to: '/partners', label: 'Partner program', key: 'partners' },
];

const FAQItem = ({ q, a, isOpen, onToggle, idx }) => (
    <div className="border-b border-neutral-200" data-testid={`faq-item-${idx}`}>
        <button type="button" onClick={onToggle} className="w-full flex items-start justify-between gap-6 py-6 text-left group" aria-expanded={isOpen} data-testid={`faq-toggle-${idx}`}>
            <h3 className="text-lg md:text-xl font-bold text-neutral-900 group-hover:text-brand-primary transition-colors leading-snug pr-4">{q}</h3>
            <span className="flex-shrink-0 mt-1 w-8 h-8 flex items-center justify-center border border-neutral-300 group-hover:border-brand-primary transition-colors">
                {isOpen ? <Minus className="h-4 w-4 text-brand-primary" /> : <Plus className="h-4 w-4 text-neutral-500" />}
            </span>
        </button>
        <div className={`overflow-hidden transition-[max-height,opacity] duration-300 ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="pb-6 pr-12 text-base md:text-lg text-neutral-600 leading-relaxed" data-testid={`faq-answer-${idx}`}>{a}</div>
        </div>
    </div>
);

const useFaqJsonLd = (faqs) => {
    useEffect(() => {
        if (!faqs.length) return undefined;
        const el = document.createElement('script');
        el.id = 'faq-jsonld';
        el.type = 'application/ld+json';
        el.textContent = JSON.stringify({
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        });
        document.head.appendChild(el);
        return () => el.remove();
    }, [faqs]);
};

const FAQ = () => {
    const [openIdx, setOpenIdx] = useState(0);
    const content = usePageContent();
    const faqs = content?.faqs || [];
    useFaqJsonLd(faqs);
    useSEO({
        title: 'Dry Ice Blasting Equipment FAQ — Buying, Rentals, Training & Support',
        description: 'Answers about buying dry ice blasting machines and parts, shipping, financing, rentals, operator training, repairs and the Dry Ice Bulls partner program.',
    });

    return (
        <div className="pt-24 pb-24" data-testid="faq-page">
            <div className="container-page">
                <div className="label-caps text-brand-primary mb-4">/ Frequently Asked Questions</div>
                <h1 className="font-display font-black uppercase text-4xl md:text-6xl leading-[0.98] text-neutral-900" data-testid="faq-heading">
                    Equipment<br /><span className="text-brand-primary">FAQ</span>
                </h1>
                <p className="mt-6 max-w-3xl text-base md:text-lg text-neutral-600 leading-relaxed">
                    Buying, shipping, financing, rentals, training and support — the questions we hear most from operators.
                </p>

                <section className="mt-14 max-w-4xl border-t border-neutral-200" data-testid="faq-list">
                    {faqs.map((f, i) => (
                        <FAQItem key={i} idx={i} q={f.q} a={f.a} isOpen={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
                    ))}
                </section>

                <section className="mt-20 max-w-4xl">
                    <div className="label-caps text-brand-primary mb-4">/ Keep exploring</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {RELATED.map((r) => (
                            <Link key={r.key} to={r.to} className="border border-neutral-200 hover:border-brand-primary bg-white p-4 label-caps text-sm text-neutral-800 hover:text-brand-primary transition-colors" data-testid={`faq-link-${r.key}`}>{r.label}</Link>
                        ))}
                    </div>
                </section>
            </div>

            <CTASection
                variant="knowledge"
                heading="Didn't find your answer?"
                subheading="Text or WhatsApp us anytime — we answer within 24 hours."
                context={{ trackingLocation: 'faq_bottom', subject: 'FAQ question — need specialist help' }}
                testid="faq-cta-bottom"
                className="mt-8"
            />
        </div>
    );
};

export default FAQ;
